import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError, request } from '../api/client'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-brand">
          <div className="brand-mark" style={{ width: 36, height: 36, fontSize: 19 }}>📦</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Fleey</div>
            <div className="brand-sub">Customer Portal</div>
          </div>
        </div>
        <h1 style={{ fontSize: 17, margin: '0 0 12px' }}>{title}</h1>
        {children}
      </div>
    </div>
  )
}

/** Ask for a link to choose a new password. Says the same for any address. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await request('/portal/auth/forgot', { method: 'POST', body: { email }, retryOn401: false })
      setSent(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the link. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Forgot your password?">
      {sent ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="banner banner-ok">If {email} has a portal login, a link to choose a new password is on its way. It lasts an hour.</div>
          <Link to="/login">Back to sign in</Link>
        </div>
      ) : (
        <form className="stack" style={{ gap: 13 }} onSubmit={submit}>
          {error && <div className="banner banner-error">{error}</div>}
          <div className="field">
            <label className="label" htmlFor="forgot-email">Email</label>
            <input id="forgot-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
          </div>
          <button className="btn btn-primary" disabled={busy} style={{ padding: 10 }}>{busy ? 'Sending…' : 'Send me a link'}</button>
          <Link to="/login" style={{ textAlign: 'center', fontSize: 13 }}>Back to sign in</Link>
        </form>
      )}
    </Card>
  )
}

/** From an invitation or reset link: choose the password. */
export function SetPasswordPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== again) return setError('The two passwords are different.')
    setBusy(true)
    setError(null)
    try {
      await request('/portal/auth/set-password', { method: 'POST', body: { token, password }, retryOn401: false })
      setDone(true)
    } catch (err) {
      if (err instanceof ApiError) {
        const fields = Object.values(err.fieldErrors?.() ?? {})
        setError(fields.length > 0 ? fields.join(' ') : err.message)
      } else {
        setError('Could not set the password. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <Card title="This link is incomplete">
        <p className="page-sub">Open the link from your email again, or ask for a new one.</p>
        <Link to="/forgot-password">Ask for a new link</Link>
      </Card>
    )
  }

  return (
    <Card title={done ? 'Password set' : 'Choose your password'}>
      {done ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="banner banner-ok">Done. Sign in with your email and this password.</div>
          <button className="btn btn-primary" onClick={() => navigate('/login', { replace: true })}>Sign in</button>
        </div>
      ) : (
        <form className="stack" style={{ gap: 13 }} onSubmit={submit}>
          {error && <div className="banner banner-error">{error}</div>}
          <div className="field">
            <label className="label" htmlFor="new-password">New password</label>
            <input id="new-password" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required autoComplete="new-password" />
            <span className="page-sub">At least 10 characters.</span>
          </div>
          <div className="field">
            <label className="label" htmlFor="new-password-again">The same again</label>
            <input id="new-password-again" className="input" type="password" value={again} onChange={(e) => setAgain(e.target.value)} minLength={10} required autoComplete="new-password" />
          </div>
          <button className="btn btn-primary" disabled={busy} style={{ padding: 10 }}>{busy ? 'Saving…' : 'Set password'}</button>
        </form>
      )}
    </Card>
  )
}
